import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Exam|Sections|Form|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamSectionCatalog,
	portal: 'SP',
};

type LocNameStart = 'SPCatalogsExamSectionsForm';
export type SPCatalogsExamSectionsFormLocNames =
	| `${LocNameStart}ButtonFormFieldEdit`
	| `${LocNameStart}ButtonFormFieldIgnore`
	| `${LocNameStart}ButtonToggleFormFieldEditability`
	| `${LocNameStart}LabelExamForm`
	| `${LocNameStart}LabelSectionName`
	| `${LocNameStart}LabelLabel`
	| `${LocNameStart}LabelCoverage`
	| `${LocNameStart}LabelHighScore`
	| `${LocNameStart}LabelMidScore`
	| `${LocNameStart}LabelLowScore`
	| `${LocNameStart}LabelCutScore`
	| `${LocNameStart}LabelMaxScore`
	| `${LocNameStart}ErrorExamFormRequired`
	| `${LocNameStart}ErrorSectionNameRequired`
	| `${LocNameStart}ErrorLabelRequired`
	| `${LocNameStart}ErrorHighScoreInvalid`
	| `${LocNameStart}ErrorMidScoreInvalid`
	| `${LocNameStart}ErrorLowScoreInvalid`
	| `${LocNameStart}ErrorCutScoreInvalid`
	| `${LocNameStart}ErrorMaxScoreInvalid`
	| `${LocNameStart}ContentCountLabel`
	| `${LocNameStart}ContentNoneSelected`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonSaveSelected`
	| `${LocNameStart}ButtonDeleteSelected`
	| `${LocNameStart}NotificationDeleteConfirmation`
	| `${LocNameStart}NotificationInvalidHighMidLow`
	| `${LocNameStart}NotificationCutLessThanMax`
	| `${LocNameStart}NotificationMissingHighOrMax`;

export const spCatalogsExamSectionFormLocs: LocDefs<SPCatalogsExamSectionsFormLocNames> = {
	SPCatalogsExamSectionsFormButtonFormFieldEdit: {
		defaultValue: 'Edit',
		description: 'Label for the button that allows for form field editability.',
		displayPath: `${pathStart}Form Field Edit`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Title.FormFieldEdit',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormButtonFormFieldIgnore: {
		defaultValue: 'Ignore',
		description: 'Label for the button that disallows for form field editability.',
		displayPath: `${pathStart}Form Field Ignore`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Title.FormFieldIgnore',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormButtonToggleFormFieldEditability: {
		defaultValue: 'Toggle Field Editability',
		description: 'Accessibility text that toggle field editability.',
		displayPath: `${pathStart}Toggle Field Editability`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.FormFieldEditability',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormLabelExamForm: {
		defaultValue: 'Exam Form',
		description: 'Label for: Exam Form.',
		displayPath: `${pathStart}Exam Form`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.ExamForm',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormLabelSectionName: {
		defaultValue: 'Section Name',
		description: 'Label for: Section Name.',
		displayPath: `${pathStart}Section Name`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.SectionName',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormLabelLabel: {
		defaultValue: 'Label',
		description: 'Label for: Label.',
		displayPath: `${pathStart}Label`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.Label',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormLabelCoverage: {
		defaultValue: 'Coverage',
		description: 'Label for: Coverage.',
		displayPath: `${pathStart}Coverage`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.Coverage',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormLabelHighScore: {
		defaultValue: 'High Score',
		description: 'Label for: High Score.',
		displayPath: `${pathStart}High Score`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.HighScore',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormLabelMidScore: {
		defaultValue: 'Mid Score',
		description: 'Label for: Mid Score.',
		displayPath: `${pathStart}Mid Score`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.MidScore',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormLabelLowScore: {
		defaultValue: 'Low Score',
		description: 'Label for: Low Score.',
		displayPath: `${pathStart}Low Score`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.LowScore',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormLabelCutScore: {
		defaultValue: 'Cut Score',
		description: 'Label for: Cut Score.',
		displayPath: `${pathStart}Cut Score`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.CutScore',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormLabelMaxScore: {
		defaultValue: 'Max Score',
		description: 'Label for: Max Score.',
		displayPath: `${pathStart}Max Score`,
		displayType: 'Label',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldLabel.MaxScore',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormErrorExamFormRequired: {
		defaultValue: 'Exam form is required',
		description: 'Error: Exam form is required.',
		displayPath: `${pathStart}Exam form is required`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.ExamFormRequired',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormErrorSectionNameRequired: {
		defaultValue: 'Section name is required',
		description: 'Error: Section name is required.',
		displayPath: `${pathStart}Section name is required`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.SectionNameRequired',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormErrorLabelRequired: {
		defaultValue: 'Label is required',
		description: 'Error: Label is required.',
		displayPath: `${pathStart}Label is required`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.LabelRequired',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormErrorHighScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error for high score: Must be a number.',
		displayPath: `${pathStart}High Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.HighScoreInvalid',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormErrorMidScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error for mid score: Must be a number.',
		displayPath: `${pathStart}Mid Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.MidScoreInvalid',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormErrorLowScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error for low score: Must be a number.',
		displayPath: `${pathStart}Low Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.LowScoreInvalid',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormErrorCutScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error for cut score: Must be a number.',
		displayPath: `${pathStart}Cut Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.CutScoreInvalid',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormErrorMaxScoreInvalid: {
		defaultValue: 'Must be a number',
		description: 'Error for max score: Must be a number.',
		displayPath: `${pathStart}Max Score Invalid`,
		displayType: 'Error',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.MaxScoreInvalid',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormContentCountLabel: {
		defaultValue: 'Sections selected:',
		description: 'Label for the selected section.',
		displayPath: `${pathStart}Selected Sections`,
		displayType: 'Content',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Content.CountLabel',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormContentNoneSelected: {
		defaultValue: 'No sections selected',
		description: 'No sections selected label.',
		displayPath: `${pathStart}No sections selected`,
		displayType: 'Content',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Content.NoneSelected',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Cancel button.',
		displayPath: `${pathStart}Cancel`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.Cancel',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormButtonSaveSelected: {
		defaultValue: 'Apply Changes to Selected Sections',
		description: 'Apply Changes to Selected Sections button.',
		displayPath: `${pathStart}Apply Changes to Selected Sections`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.SaveSelected',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormButtonDeleteSelected: {
		defaultValue: 'Delete Selected Sections',
		description: 'Delete Selected Sections button.',
		displayPath: `${pathStart}Delete Selected Sections`,
		displayType: 'Button',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.Button.DeleteSelected',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormNotificationDeleteConfirmation: {
		defaultValue: 'Are you sure you want to delete the following number of sections?',
		description: 'Delete confirmation text.',
		displayPath: `${pathStart}Delete Confirmation`,
		displayType: 'Notification',
		legacyName:
			'Exam.SectionCatalog/Catalogs.Exams.Sections.NotificationMessage.DeleteConfirmation',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormNotificationInvalidHighMidLow: {
		defaultValue: 'High, Mid, and Low scores must be in the correct order.',
		description: 'Invalid high, mid, and low scores text.',
		displayPath: `${pathStart}Invalid High, Mid, and Low Scores`,
		displayType: 'Notification',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.ScoresNumeric',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormNotificationCutLessThanMax: {
		defaultValue: 'Max score must be greater than or equal to cut score.',
		description: 'Invalid cut score text.',
		displayPath: `${pathStart}Invalid Cut Score`,
		displayType: 'Notification',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.MaxGreaterThanCut',
		...sharedProperties,
	},
	SPCatalogsExamSectionsFormNotificationMissingHighOrMax: {
		defaultValue: 'High or Max score is required.',
		description: 'Missing high or max score text.',
		displayPath: `${pathStart}Missing High or Max Score`,
		displayType: 'Notification',
		legacyName: 'Exam.SectionCatalog/Catalogs.Exams.Sections.FieldError.ScoresRequired',
		...sharedProperties,
	},
};

export type SPCatalogsExamSectionsFormLocType = LocMap<SPCatalogsExamSectionsFormLocNames>;
export type ExamSectionValidationErrors = {
	invalidHighMidLow: boolean;
	cutLessThanMax: boolean;
	missingHighOrMax: boolean;
};
